import "./Pulse.css";

import { Box, Button, CloseButton, PlacementWithLogical, Stack, Text, usePopper } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { OnboardingConfirmCancel } from "./OnboardingConfirmCancel";
import { OnboardingImage } from "./OnboardingImage";

interface OnboardingModalProps {
  id: string;
  image?: string;
  placement: PlacementWithLogical;
  target?: string;
  title: string;
  description: string;
  actionLabel?: string;
  stepsTotal: number;
  currentStep: number;
  onGotIt?: () => void;
  onToggleOnboarding: (shouldShow: boolean) => void;
}

function isHidden(el: HTMLDivElement | null) {
  if (!el) return true;
  return el.offsetParent === null;
}

export const OnboardingStep = (props: OnboardingModalProps) => {
  const {
    image,
    stepsTotal,
    currentStep,
    title,
    description,
    target,
    placement,
    actionLabel = "Next",
    onGotIt,
    onToggleOnboarding,
  } = props;
  const { popperRef, referenceRef } = usePopper({ placement, offset: [8, -16] });
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const elements = target ? (Array.from(document.querySelectorAll(target)) as HTMLDivElement[]) : [];
  const externalElement = elements.length > 0 ? elements[elements.length - 1] : null;
  const isVisible = externalElement && !isHidden(externalElement);

  useEffect(() => {
    referenceRef(externalElement);
  }, [externalElement, referenceRef]);

  return (
    <>
      {!showCancelConfirmation && isVisible && target && (
        <Box className="pulse" position="absolute" ref={popperRef} borderRadius={"10px"} width="12px" height="12px" />
      )}
      {!showCancelConfirmation && (
        <Stack
          borderRadius={"0.5rem"}
          color="foreground.inverse"
          position="absolute"
          bottom="2"
          right="8"
          background="foreground.primary"
          padding="1.5rem"
          width="25rem"
          gap="0"
        >
          <CloseButton
            padding="0"
            position="absolute"
            right="1rem"
            top="1.25rem"
            onClick={() => setShowCancelConfirmation(true)}
          />
          <Stack gap="1.5rem">
            <Text color="foreground.inverse" textStyle="label/L/medium">
              {title}
            </Text>
            <Text textStyle="body/M" whiteSpace="pre-wrap">
              {description}
            </Text>
            {image && <OnboardingImage src={image} />}
          </Stack>
          <Stack mt={"2rem"} direction="row" justifyContent="space-between" alignItems="center">
            <Text>
              {currentStep} of {stepsTotal}
            </Text>
            <Button
              variant="primary"
              onClick={() => {
                onGotIt?.();
              }}
            >
              {actionLabel}
            </Button>
          </Stack>
        </Stack>
      )}
      {showCancelConfirmation && (
        <OnboardingConfirmCancel
          onCancel={() => setShowCancelConfirmation(false)}
          onToggleOnboarding={onToggleOnboarding}
        />
      )}
    </>
  );
};
